import React, { useEffect, useMemo, useState } from 'react';
import { LineChartWidget } from './index';
import { NonIdealState } from '@blueprintjs/core';
import uniq from 'lodash-es/uniq';
import * as d3 from 'd3';
import { DefaultChartColorScales } from 'styles/chartColors';
import { NO_DATA_MESSAGE } from 'widgets/chartUtils';
import { useWidgetControlsContext } from 'views/common/WidgetControls';

/**
 * ChartWrapper for the Line Chart Graph.
 *
 * @param getColorScale
 * @param dimensions
 * @param data
 * @param widgetConfiguration
 * @returns {*}
 * @constructor
 */
export function LineChartWrapper({ getColorScale, dimensions, data, widgetConfiguration, noDataMessage=NO_DATA_MESSAGE }:LineChartWrapperProps) {
	const [convertedData, setConvertedData] = useState(null);
	const {dispatch, state} = useWidgetControlsContext();
	const hideLabels = state?.hideLabels;
	useEffect(() => {
		// the data is null or in the wrong shape and we exit
		if (!data || !Array.isArray(data?.entries)) {
			dispatch && dispatch({ type: "setLabelsToggleBtnStatus", payload: false });
			return;
		}

		// empty data is "valid"
		if (data.entries?.length === 0) {
			dispatch && dispatch({ type: "setLabelsToggleBtnStatus", payload: false });
			setConvertedData({ data: [], properties: [] });
			return;
		}

		dispatch && dispatch({ type: "setLabelsToggleBtnStatus", payload: true });

		// need to determine if there is more than 1 group
		const singleGroup = !data.entries[0].children;

		const converted = {
			lineKeys: [],
			lineValues: [],
			lines: {},
			extent: [0, 0],
			rolled: null,
			colorPropertyMap: {}
		};

		if (singleGroup) {
			// at this point we don't know what the label of the line should be, since we don't have that information
			// TODO: pull this from the endpointQuery
			converted.lineKeys.push(['default']);
			converted.lines['default'] = data.entries.map((e) => {
				converted.lineValues.push(e.key);
				converted.extent[1] = Math.max(converted.extent[1], e.value);
				return { x: e.key, y: e.value };
			});
			converted.lines['default'] = converted.lines['default'].sort((a, b) => d3.ascending(a.x, b.x));
		} else {
			// TODO: this should eventually be given to us at the top level, by the server
			converted.lineKeys = data.entries.map((e) => e.key);
			data.entries.forEach((entry) => {
				converted.lines[entry.key] = entry.children.map((e) => {
					converted.extent[1] = Math.max(converted.extent[1], e.value);
					converted.lineValues.push(e.key);
					return { x: e.key, y: e.value };
				});
				if(!data.sorted){
					converted.lines[entry.key] = converted.lines[entry.key].sort((a, b) => d3.ascending(a.x, b.x));
				}
			});
		}

		converted.lineValues = data.sorted? uniq(converted.lineValues) : uniq(converted.lineValues).sort();
		converted.rolled = converted.lineKeys.map((k) => {
			return { key: k };
		});

		converted.colorPropertyMap = {};
		if (widgetConfiguration?.colorScale === DefaultChartColorScales.Sequential) {
			getColorScale.domain([0, converted?.lineKeys?.length || 0]);
			converted.lineKeys.forEach((d, index) => {
				converted.colorPropertyMap[d] = index;
			});
		} else {
			getColorScale.domain(converted?.lineKeys);
			converted.lineKeys.forEach((d) => {
				converted.colorPropertyMap[d] = d;
			});
		}

		setConvertedData(converted);
	}, [data, getColorScale, widgetConfiguration]);

	// If the state is not ideal (the data is null or empty) render the nonIdealState message
	return convertedData?.lineKeys?.length > 0 ? (
		<LineChartWidget data={convertedData}
						 dimensions={dimensions}
						 getColor={getColorScale}
						 hideLabels={hideLabels}
						 yAxisLabel={widgetConfiguration.yAxisLabel}
						 legendKeys={widgetConfiguration.hasOwnProperty('legendKeys') ? widgetConfiguration.legendKeys : undefined}
						 />
	) : (
		<NonIdealState
			title="No Data Found"
			icon="graph-remove"
			description={noDataMessage}
		/>
	);
}

export interface LineChartWrapperProps { 
	getColorScale, 
	dimensions, 
	data: MultiLineChartData | any, 
	widgetConfiguration, 
	noDataMessage,
}

interface MultiLineChartData {
	entries: {
		children:{
			count,
			key,
			value
		}[],
		key
	}[]
}
